import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { GoogleReCaptchaProvider, withGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { toast } from 'react-toastify';
import cn from 'classnames';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
} from 'reactstrap';

import Input from 'components/Form/Input';
import Validator from 'components/Validator/Validator';
import { Button } from 'components/Button';
import { ButtonLink } from 'components/Button/ButtonLink';

import { forgotUsername as forgotUsernameAction } from 'actions/dashboard';
import { useConfig } from 'config/ConfigContext';

import { ssnLast4Mask } from 'utils/masks';

import forgotUsernameSchema from './forgotUsernameSchema';
import style from './style.scss';

const RECAPTCHA_POLL_MS = 200;
const RECAPTCHA_TIMEOUT_MS = 30000;

const ForgotUsername = (props) => {
  const navigate = useNavigate();
  const { validator: { values, errors } } = props;
  const [isLoading, setIsLoading] = useState(false);
  const hasSucceeded = useRef(false);
  // The provider mounts with this page, so executeRecaptcha throws until the
  // script is ready. Keep the latest function so a submit can wait for it.
  const executeRecaptchaRef = useRef(props.googleReCaptchaProps.executeRecaptcha);
  executeRecaptchaRef.current = props.googleReCaptchaProps.executeRecaptcha;

  const handleInputChange = (event) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler(event.target.name, event.target.value.trim());
  };

  const handleSendUsername = (e) => {
    e.preventDefault();
    const {
      validator: { validate },
    } = props;

    if (hasSucceeded.current || !validate(forgotUsernameSchema).isValid) return;

    const email = `${values.email}`.toLowerCase();
    const ssnLast4 = `${values.ssnLast4}`;
    setIsLoading(true);

    const recaptchaPromise = new Promise((resolve, reject) => {
      const startedAt = Date.now();
      let pollId;

      const attempt = () => {
        if (Date.now() - startedAt >= RECAPTCHA_TIMEOUT_MS) {
          clearTimeout(pollId);
          reject(new Error('reCAPTCHA is not ready'));
          return;
        }

        const executeRecaptcha = executeRecaptchaRef.current;
        if (typeof executeRecaptcha !== 'function') {
          pollId = setTimeout(attempt, RECAPTCHA_POLL_MS);
          return;
        }

        try {
          Promise.resolve(executeRecaptcha('forgot_username')).then(
            (token) => {
              clearTimeout(pollId);
              resolve(token);
            },
            (err) => {
              clearTimeout(pollId);
              reject(err);
            }
          );
        } catch {
          pollId = setTimeout(attempt, RECAPTCHA_POLL_MS);
        }
      };

      attempt();
    });

    recaptchaPromise
      .then((token) => {
        props.forgotUsername({
          data: { email, ssnLast4, token },
          success: () => {
            if (hasSucceeded.current) return;
            hasSucceeded.current = true;
            setIsLoading(false);
            toast.success('Verification code sent successfully.', {
              autoClose: 4000,
              onClose: () => navigate('/'),
            });
          },
          fail: (err) => {
            setIsLoading(false);
            const msg = err?.data?.message || 'Something went wrong. Please try again.';
            toast.error(msg);
          },
        });
      })
      .catch(() => {
        setIsLoading(false);
        toast.error('Verification could not be completed. Please try again.');
      });
  };

  const backToSignIn = (e) => {
    if (e) e.preventDefault();
    navigate('/');
  };

  return (
    <form onSubmit={handleSendUsername} style={{ minHeight: 650 }} className="d-flex justify-content-center align-items-center">
      <Container fluid>
        <Col sm={{ size: 6, offset: 3 }}>
          <Row>
            <Col>
              <Row>
                <Col>
                  <Card className="animated fadeInUp delay-300ms">
                    <CardHeader>Forgot username</CardHeader>
                    <CardBody>
                      <p>Enter the email and the last 4 digits of the SSN on file for your account and we&apos;ll send you your username.</p>
                      <Input
                        name="email"
                        label="Email"
                        onChange={handleInputChange}
                        value={values.email}
                        hasError={!!errors.email}
                        hasValue
                        placeHolder="you@email.com"
                        isBadgeVisible={false}
                        isRequired
                        errorMessage={errors.email}
                      />
                      <Input
                        name="ssnLast4"
                        label="Last 4 of SSN"
                        onChange={handleInputChange}
                        value={values.ssnLast4 || ''}
                        hasError={!!errors.ssnLast4}
                        hasValue
                        isMasked={ssnLast4Mask}
                        placeHolder="1234"
                        isBadgeVisible={false}
                        isRequired
                        errorMessage={errors.ssnLast4}
                      />
                      <p style={{ fontSize: 12, color: '#888', lineHeight: 1.5, marginBottom: 0 }}>
                        This site is protected by reCAPTCHA and the Google&nbsp;
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                        {' '}
                        and&nbsp;
                        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                        &nbsp;also apply.
                      </p>
                    </CardBody>
                  </Card>
                  <div className="text-center">
                    <Button
                      className={cn('small w-100', style.button)}
                      onClick={handleSendUsername}
                      isLoading={isLoading}
                      color="primary"
                      type="submit"
                    >
                      Send username
                    </Button>
                    <div className="mt-1">
                      <ButtonLink className="btn-link cursor-pointer" onClick={backToSignIn}>Back to sign in</ButtonLink>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Container>
    </form>
  );
};

ForgotUsername.propTypes = {
  validator: PropTypes.object.isRequired,
  googleReCaptchaProps: PropTypes.object.isRequired,
  forgotUsername: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  forgotUsername: forgotUsernameAction,
};

const ForgotUsernameForm = compose(
  Validator(forgotUsernameSchema),
  connect(null, mapDispatchToProps),
  withGoogleReCaptcha
)(ForgotUsername);

const ForgotUsernamePage = () => {
  const { reCaptchaKey } = useConfig();

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={reCaptchaKey}
      scriptProps={{ id: 'google-recaptcha-v3-forgot-username' }}
    >
      <ForgotUsernameForm />
    </GoogleReCaptchaProvider>
  );
};

export default ForgotUsernamePage;
